import React, { useState, useMemo } from 'react'
import { LoadingOutlined } from '@ant-design/icons'
import {
  Vertical,
  Horizontal,
  Texto,
  MaterialIcon,
  GraviGrid,
  GraviButton,
  addCommasToNumber,
} from '@gravitate-js/excalibrr'
import { useFormatter } from '@api/useFormatter'
import { Button, Dropdown, Menu, Radio, Tag, Result } from 'antd'
import type { MatchDetailResponse } from '../../../../../api/useSupplierReconciliation/useMatchDetail'
import { useMatchTaxData, type TaxGridRow } from '../hooks/useMatchTaxData'
import { useInlineMatchManagement } from '../hooks/useInlineMatchManagement'
import { useMatchActions } from '../../../../../api/useSupplierReconciliation/useMatchActions'
import { createTaxColumnDefs, createManageMatchColumnDefs } from '../columnDefs'
import { MatchDetailToolbar } from './MatchDetailToolbar'
import { calculateTaxPrecisions } from '../utils/precisionCalculator'

interface TaxGridsSectionProps {
  matchDetails: MatchDetailResponse
  matchId: string
  onManageMatch?: () => void
  onMappingRefresh?: () => void
}

export function TaxGridsSection({ matchDetails, matchId, onManageMatch, onMappingRefresh }: TaxGridsSectionProps) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  // State for selected sort option
  const [selectedSort, setSelectedSort] = useState<string>('')

  // Use the existing hooks for tax data and manage mode
  const { invoiceTaxData, bolTaxData, invoiceOrdering, bolOrdering } = useMatchTaxData(matchDetails, matchId)

  // Calculate precision requirements based on tax data
  const taxPrecisionData = useMemo(() => {
    const allTaxData = [...(invoiceTaxData || []), ...(bolTaxData || [])]
    return calculateTaxPrecisions(allTaxData)
  }, [invoiceTaxData, bolTaxData])

  // Get the recalculation mutation and utility functions from match actions
  const { useRecalculateMatch, canRecalculateTaxes } = useMatchActions()
  const recalculateMutation = useRecalculateMatch()

  // State for recalculation loading
  const [isRecalculating, setIsRecalculating] = useState(false)

  const {
    isManageMode,
    setIsManageMode,
    searchMode,
    switchSearchMode,
    selectedIds,
    toggleSelection,
    hasUnsavedChanges,
    hasError,
    error,
    isSaving,
    isManageLoading,
    canSwitchMode,
    saveChanges,
    cancelChanges,
  } = useInlineMatchManagement(matchId)

  // Check if match is approved
  const isMatchApproved = matchDetails?.status === 'approved'

  // Handler for recalculating taxes using real API implementation
  const handleRecalculateTaxes = async () => {
    try {
      setIsRecalculating(true)
      await recalculateMutation.mutateAsync({
        match_id: matchId,
        matchDetails,
      })
    } catch (error) {
      console.error('Tax recalculation failed:', error)
    } finally {
      setIsRecalculating(false)
    }
  }

  // Check what documents we have
  const invoiceDocuments = matchDetails?.document_detail_rows?.filter((row) => row.document_type === 'invoice') || []
  const bolDocuments = matchDetails?.document_detail_rows?.filter((row) => row.document_type === 'bol') || []
  const hasInvoices = invoiceDocuments.length > 0
  const hasBols = bolDocuments.length > 0

  // Create dynamic currency formatters
  const currencyFormatters = useMemo(
    () => ({
      formatCurrency: (amount?: number) => {
        if (amount === null || amount === undefined) return `${currencySymbol}0.00`
        if (amount === 0) return `${currencySymbol}0.00`
        const sign = amount < 0 ? '-' : ''
        return `${sign}${currencySymbol}${addCommasToNumber(Math.abs(amount), 2)}`
      },
      formatPerUnit: (amount?: number) => {
        if (amount === null || amount === undefined) return `${currencySymbol}0.0000`
        if (amount === 0) return `${currencySymbol}0.0000`
        const sign = amount < 0 ? '-' : ''
        return `${sign}${currencySymbol}${Math.abs(amount).toFixed(4)}`
      },
      currencySymbol,
    }),
    [currencySymbol]
  )

  // Create column definitions with precision support
  const taxColumns = useMemo(() => createTaxColumnDefs(currencyFormatters, taxPrecisionData.precisions), [])
  const manageMatchColumns = useMemo(() => createManageMatchColumnDefs(), [])

  return (
    <Vertical flex='1' style={{ overflow: 'hidden' }}>
      {/* Taxes Header or Save/Cancel Toolbar */}
      <MatchDetailToolbar
        isManageMode={isManageMode}
        isMatchApproved={isMatchApproved}
        hasUnsavedChanges={hasUnsavedChanges}
        hasError={hasError}
        error={error}
        isSaving={isSaving}
        selectedIds={selectedIds}
        onSave={saveChanges}
        onCancel={cancelChanges}
        onRecalculateTaxes={handleRecalculateTaxes}
        isRecalculating={isRecalculating}
        canRecalculateTaxes={canRecalculateTaxes(matchDetails)}
        onSortTaxes={async (sortField: string) => {
          // Update selected sort state
          setSelectedSort(sortField)

          // Helper function to sort tax data by field
          const sortTaxData = (data: TaxGridRow[], field: string) => {
            return [...data].sort((a, b) => {
              const aVal = a[field]
              const bVal = b[field]

              // Handle numerical sorting for amount and per_unit
              if (field === 'total' || field === 'per_unit') {
                const aNum = typeof aVal === 'string' ? parseFloat(aVal.replace(/[$,]/g, '')) : aVal
                const bNum = typeof bVal === 'string' ? parseFloat(bVal.replace(/[$,]/g, '')) : bVal
                return aNum - bNum
              }

              if (typeof aVal === 'string' && typeof bVal === 'string') {
                return aVal.localeCompare(bVal)
              }

              return 0
            })
          }

          // Sort and persist invoice tax order to localStorage
          const sortedInvoiceTaxData = sortTaxData(invoiceTaxData, sortField)
          invoiceOrdering.saveOrder(sortedInvoiceTaxData)

          // Sort and persist BOL tax order to localStorage
          const sortedBolTaxData = sortTaxData(bolTaxData, sortField)
          bolOrdering.saveOrder(sortedBolTaxData)
        }}
        selectedSort={selectedSort}
      />

      {/* Search Mode Toggle for Manage Mode */}
      {isManageMode && (
        <Horizontal
          style={{
            padding: '12px 24px',
            borderBottom: '1px solid var(--border-color)',
            background: 'var(--bg-1)',
          }}
          gap={16}
          verticalCenter
        >
          <Texto category='p2' weight='bold'>
            Search for:
          </Texto>
          <Radio.Group value={searchMode} onChange={(e) => switchSearchMode(e.target.value)} disabled={!canSwitchMode}>
            <Radio.Button value='bol'>BOL</Radio.Button>
            <Radio.Button value='invoice'>Invoice</Radio.Button>
          </Radio.Group>
          {isManageLoading && (
            <Horizontal gap={8} verticalCenter>
              <LoadingOutlined />
              <Texto category='p2' appearance='hint'>
                Loading options...
              </Texto>
            </Horizontal>
          )}
        </Horizontal>
      )}

      {/* Tax Grids */}
      <Vertical flex={1} style={{ overflow: 'hidden' }}>
        {isManageMode ? (
          // Manage Mode - Single Grid
          <GraviGrid
            columnDefs={manageMatchColumns}
            rowData={searchMode === 'invoice' ? invoiceTaxData : bolTaxData}
            agPropOverrides={{
              rowSelection: 'multiple',
              suppressRowClickSelection: false,
              rowGroupPanelShow: 'never',
              onSelectionChanged: (params) => {
                const selectedRows = params.api.getSelectedRows()
                const currentSelectedIds = selectedRows.map((row) => row.id)

                selectedIds.forEach((id) => {
                  if (!currentSelectedIds.includes(id)) {
                    toggleSelection(id)
                  }
                })
              },
            }}
          />
        ) : (
          // Normal Mode - Dual Grids
          <Horizontal flex={1} style={{ overflow: 'visible' }}>
            {/* Invoice Tax Grid */}
            <Vertical flex={1} style={{ overflow: 'hidden' }}>
              <Vertical
                bg='elevated'
                border='right'
                flex='0'
                style={{ overflow: 'visible', padding: '12px 16px', borderBottom: '1px solid var(--border-color)' }}
              >
                <Texto category='h5' weight='bold'>
                  INVOICE TAXES
                </Texto>
              </Vertical>
              <Vertical flex={1}>
                {hasInvoices ? (
                  <GraviGrid
                    columnDefs={taxColumns}
                    rowData={invoiceTaxData}
                    agPropOverrides={{
                      headerHeight: 40,
                      rowHeight: 35,
                      rowDragManaged: !isMatchApproved,
                      animateRows: true,
                      suppressRowDrag: isMatchApproved,
                      onRowDragEnd: invoiceOrdering.handleRowDragEnd,
                    }}
                  />
                ) : (
                  <Result
                    status='warning'
                    title='No invoices found for this match'
                    subTitle='Add invoices to this match to view tax transaction details.'
                    extra={
                      <GraviButton
                        theme2
                        disabled={isMatchApproved}
                        icon={<MaterialIcon icon='link' className='pt-1' />}
                        onClick={onManageMatch}
                      >
                        Manage Match
                      </GraviButton>
                    }
                  />
                )}
              </Vertical>
            </Vertical>

            {/* BOL Tax Grid */}
            <Vertical flex={1} style={{ overflow: 'hidden' }}>
              <Vertical
                bg='elevated'
                flex='0'
                style={{ overflow: 'visible', padding: '12px 16px', borderBottom: '1px solid var(--border-color)' }}
              >
                <Texto category='h5' weight='bold'>
                  BOL TAXES
                </Texto>
              </Vertical>
              <Vertical flex={1}>
                {hasBols ? (
                  <GraviGrid
                    columnDefs={taxColumns}
                    rowData={bolTaxData}
                    agPropOverrides={{
                      domLayout: 'normal',
                      headerHeight: 40,
                      rowHeight: 35,
                      rowDragManaged: !isMatchApproved,
                      animateRows: true,
                      suppressRowDrag: isMatchApproved,
                      onRowDragEnd: bolOrdering.handleRowDragEnd,
                    }}
                  />
                ) : (
                  <Result
                    status='warning'
                    title='No BOLs found for this match'
                    subTitle='Add BOLs to this match to view tax transaction details.'
                    extra={
                      <GraviButton
                        theme2
                        disabled={isMatchApproved}
                        icon={<MaterialIcon icon='link' className='pt-1' />}
                        onClick={onManageMatch}
                      >
                        Manage Match
                      </GraviButton>
                    }
                  />
                )}
              </Vertical>
            </Vertical>
          </Horizontal>
        )}
      </Vertical>
    </Vertical>
  )
}
